import React, { useEffect, useState } from 'react';
import { Row, Col, Card, CardBody, Container, Spinner } from 'reactstrap';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';

import Breadcrumbs from '../../components/Common/Breadcrumb';
import './landing-guide.css';

// La guida esiste in italiano e inglese; ogni altra lingua del CRM ricade
// sull'inglese. Aggiungere una lingua = aggiungere un file in public/guides.
const fileFor = (language) =>
  String(language || '').toLowerCase().startsWith('it')
    ? '/guides/landing-guide.it.md'
    : '/guides/landing-guide.en.md';

// react-markdown non assegna id agli heading (serve un plugin come
// rehype-slug, non installato qui). Le sezioni della guida che devono essere
// raggiungibili con un'ancora (es. da ComponentSettingsPanel) terminano la
// riga con "{#slug}"; questo estrae lo slug dall'ultimo nodo di testo dei
// children e lo toglie dal testo visibile, così l'heading espone un id reale
// senza dipendenze aggiuntive. Uguale in entrambe le lingue: gli slug sono
// identici nei due file .md, i link che puntano qui non cambiano con la lingua.
const HEADING_ID_PATTERN = /\s*\{#([a-z0-9-]+)\}\s*$/i;

const splitHeadingId = (children) => {
  const items = React.Children.toArray(children);
  const lastIndex = items.length - 1;
  const last = items[lastIndex];
  if (typeof last === 'string') {
    const match = last.match(HEADING_ID_PATTERN);
    if (match) {
      const stripped = last.slice(0, match.index);
      const next = [...items];
      if (stripped) {
        next[lastIndex] = stripped;
      } else {
        next.splice(lastIndex, 1);
      }
      return { id: match[1], children: next };
    }
  }
  return { id: undefined, children: items };
};

const makeHeading = (Tag) => {
  const Heading = ({ node, children, ...props }) => {
    const { id, children: cleaned } = splitHeadingId(children);
    return (
      <Tag id={id} {...props}>
        {cleaned}
      </Tag>
    );
  };
  return Heading;
};

const HEADING_COMPONENTS = {
  h1: makeHeading('h1'),
  h2: makeHeading('h2'),
  h3: makeHeading('h3'),
};

const LandingGuide = () => {
  const { i18n } = useTranslation();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(fileFor(i18n.language))
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((text) => {
        if (!cancelled) setContent(text);
      })
      .catch(() => {
        if (!cancelled) setContent('# Guide unavailable\n\nCould not load the guide file.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  // The browser's own fragment scroll runs at document load, before this
  // page's content has been fetched — it finds nothing and gives up
  // silently. VerticalLayout's componentDidMount also forces
  // window.scrollTo(0, 0) unconditionally on mount, which would undo a scroll
  // attempted any earlier than this anyway. So the jump to the anchor has to
  // be redone by hand, once the markdown (and therefore the target heading)
  // is actually in the DOM — i.e. after `loading` turns false, not in the
  // same tick as setContent. No-op if the hash names nothing.
  useEffect(() => {
    if (loading) return;
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return;
    const target = document.getElementById(hash);
    if (target) target.scrollIntoView();
  }, [loading]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Landing Guide" loading={loading} />
          <Row>
            <Col className="col-12">
              <Card>
                <CardBody>
                  {loading ? (
                    <div className="text-center p-5">
                      <Spinner color="secondary" />
                    </div>
                  ) : (
                    <div className="landing-guide">
                      <ReactMarkdown components={HEADING_COMPONENTS}>
                        {content}
                      </ReactMarkdown>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default LandingGuide;
